import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import clsx from 'clsx';
import _omit from 'lodash/omit';

import styles from './styles.module.scss';

import { MoreFiltersButton } from '@components/search/moreFilters';
import { SearchPickerWithControls } from '@components/_shared/datePickers/searchPickerWithControls';
import { useSearchListings } from '@utils/context/searchListings';
import { BaseComponentProps } from '@utils/types/baseComponents';
import { MarketType } from '@utils/types/city';
import { NeighborhoodType } from '@utils/types/neighborhoods';
import { BedroomSelectors } from '@components/search/bedroomSelectors';
import { PetCheckbox } from '@components/search/petCheckbox';
import { FilterParamsType } from '@utils/types/searchListingsContext';
import { SearchField } from '@components/search/search';
import { MobileFilters } from '@components/search/mobileFilters';
import { LocationSelectors } from '@components/search/locationSelectors';
import { PriceSelector } from '@components/search/priceSelector';
import { Global } from '@utils/constants/global';

type Props = {
  markets: MarketType[];
  neighborhoods: NeighborhoodType[];
} & BaseComponentProps;

export function SearchBar(props: Props) {
  const { containerStyle, markets, neighborhoods } = props;

  const {
    listings,
    filterParams,
    changeMarket,
    changeDates,
    changeFuzzyDatesOffset,
    changePrice,
    changeNeighborhoods,
    changeBedrooms,
    changePetFriendly,
    changeBulkFilters,
    changeSearch,
  } = useSearchListings();

  const containerRef = useRef<HTMLDivElement>(null);
  const [filtersToShow, setFiltersToShow] = useState({
    price: true,
    bedrooms: true,
    petFriendly: true,
  });

  const { query } = useRouter();

  useEffect(() => {
    const filtersFromParams: FilterParamsType = {
      fuzzyDatesOffset: {
        from: query.offsetFrom
          ? parseInt(query.offsetFrom as string)
          : Global.FUZZY_DATE_OFFSET_DEFAULT,
        to: query.offsetTo
          ? parseInt(query.offsetTo as string)
          : Global.FUZZY_DATE_OFFSET_DEFAULT,
      },
      search: query.search ? decodeURIComponent(query.search as string) : '',
    };

    const preselectedDateRange = query.dates
      ? {
          from: new Date(query.dates[0]),
          to: new Date(query.dates[1]),
        }
      : undefined;

    if (
      preselectedDateRange &&
      preselectedDateRange.from &&
      preselectedDateRange.to
    ) {
      filtersFromParams.dates = {
        from: new Date(preselectedDateRange.from),
        to: new Date(preselectedDateRange.to),
      };
    }

    changeBulkFilters(filtersFromParams);
  }, []);

  useEffect(() => {
    if (query.market && query.market !== filterParams.market) {
      changeMarket(
        query.market === 'miami-'
          ? 'miami-&-fort-lauderdale'
          : (query.market as string)
      );
    }
  }, [query.market]);

  useEffect(() => {
    if (containerRef.current) {
      const datesWidth = 266;
      const searchWidth = 267;
      const moreFiltersWidth = 91;
      const marketSelectorsMaxWidth =
        filterParams.market === 'new-york-city' ? 436 : 201;
      const priceMaxWidth = 146;
      const bedroomsMaxWidth = 166;
      const petFriendlyMaxWidth = 131;

      let freeSpace =
        containerRef.current.offsetWidth -
        datesWidth -
        searchWidth -
        moreFiltersWidth -
        marketSelectorsMaxWidth;

      const filtersToShow = {
        price: false,
        bedrooms: false,
        petFriendly: false,
      };

      if (freeSpace >= priceMaxWidth) {
        filtersToShow.price = true;
        freeSpace -= priceMaxWidth;
      }

      if (freeSpace >= bedroomsMaxWidth) {
        filtersToShow.bedrooms = true;
        freeSpace -= bedroomsMaxWidth;
      }

      if (freeSpace >= petFriendlyMaxWidth) {
        filtersToShow.petFriendly = true;
      }

      setFiltersToShow(filtersToShow);
    }
  }, [filterParams]);

  const applyMoreFilters = (filterValues: FilterParamsType) => {
    if (filterValues.market) {
      changeMarket(
        filterValues.market === 'miami-'
          ? 'miami-&-fort-lauderdale'
          : filterValues.market
      );
    }
    changeBulkFilters(_omit(filterValues, ['market']));
  };

  return (
    <div className={clsx([styles.searchBar, containerStyle])}>
      <div className={styles.filtersBar} ref={containerRef}>
        <div className={styles.filters}>
          <SearchPickerWithControls
            value={filterParams.dates}
            onRangeSelect={changeDates}
            defaultFuzzyDatesOffset={filterParams.fuzzyDatesOffset}
            onFuzzyDatesOffsetApply={changeFuzzyDatesOffset}
            buttonHasBorder
            containerStyle={styles.rangePicker}
            minAllowedDays={
              filterParams.market === 'new-york-city' ? 30 : undefined
            }
          />

          <div className={styles.separator} />

          <div className={styles.filters__selectors}>
            <LocationSelectors
              markets={markets}
              neighborhoods={neighborhoods}
              filters={filterParams}
              changeMarket={changeMarket}
              changeNeighborhoods={changeNeighborhoods}
              containerStyle={styles.locationSelectors}
            />

            {filtersToShow.price && (
              <PriceSelector
                value={filterParams.price}
                onChange={changePrice}
                containerStyle={styles.price}
                isMonthly={filterParams.market === 'new-york-city'}
              />
            )}

            {filtersToShow.bedrooms && (
              <BedroomSelectors
                value={filterParams.bedrooms}
                onChange={changeBedrooms}
                containerStyle={styles.bedrooms}
              />
            )}

            {filtersToShow.petFriendly && (
              <PetCheckbox
                value={filterParams.petFriendly}
                onChange={changePetFriendly}
                containerStyle={styles.petCheckbox}
              />
            )}

            <MoreFiltersButton
              activeFilters={filterParams}
              onApply={applyMoreFilters}
              containerStyle={styles.moreFiltersButton}
              filtersFromSearchbarToShow={{
                price: !filtersToShow.price,
                bedrooms: !filtersToShow.bedrooms,
                petFriendly: !filtersToShow.petFriendly,
              }}
            />

            <SearchField
              defaultValue={filterParams.search}
              onSubmit={changeSearch}
              containerStyle={styles.search}
            />
          </div>
        </div>
      </div>

      <div className={styles.mobileBar}>
        <div className={styles.count}>
          {`${listings.length} Homes`}&nbsp;
          <span>match your search</span>
        </div>

        <MobileFilters
          activeFilters={filterParams}
          onApply={applyMoreFilters}
          markets={markets}
          neighborhoods={neighborhoods}
        />
      </div>
    </div>
  );
}
